import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  IconDashboard, IconScan, IconInventory, IconOrders, IconVoice,
  IconFarmers, IconBar, IconSupplies, IconUsers, IconVendors, IconSignOut,
  IconX, IconSettings, IconMenu, ParadiseOpsLogo,
} from './Icons';
import VoiceInterface from './VoiceInterface';

// Nav entries support { section: 'Label' } dividers alongside route items
const NAV_BY_ROLE = {
  chef: [
    { to: '/chef', label: 'Dashboard', Icon: IconDashboard },
    { section: 'Kitchen' },
    { to: '/chef/inventory', label: 'Inventory', Icon: IconInventory },
    { to: '/chef/scan', label: 'Scan Invoice', Icon: IconScan },
    { to: '/chef/orders', label: 'Orders', Icon: IconOrders },
    { section: 'Tools' },
    { to: '/chef/voice', label: 'Voice', Icon: IconVoice },
  ],
  gm: [
    { to: '/gm', label: 'Dashboard', Icon: IconDashboard },
    { section: 'Operations' },
    { to: '/gm/inventory', label: 'Inventory', Icon: IconInventory },
    { to: '/gm/orders', label: 'Approvals', Icon: IconOrders },
    { section: 'Sourcing' },
    { to: '/gm/farmers', label: 'Farmers', Icon: IconFarmers },
    { section: 'Tools' },
    { to: '/gm/voice', label: 'Voice', Icon: IconVoice },
  ],
  bartender: [
    { to: '/bartender', label: 'Dashboard', Icon: IconDashboard },
    { to: '/bartender/inventory', label: 'Bar Stock', Icon: IconBar },
  ],
  supplies: [
    { to: '/supplies', label: 'Dashboard', Icon: IconDashboard },
    { to: '/supplies/orders', label: 'Approvals', Icon: IconOrders },
    { to: '/supplies/inventory', label: 'Supplies', Icon: IconSupplies },
  ],
  restaurant_admin: [
    { to: '/admin', label: 'Dashboard', Icon: IconDashboard },
    { section: 'Inventory' },
    { to: '/admin/inventory', label: 'Inventory', Icon: IconInventory },
    { to: '/admin/scan', label: 'Scan Invoice', Icon: IconScan },
    { to: '/admin/invoices', label: 'Invoice History', Icon: IconScan },
    { section: 'Ordering' },
    { to: '/admin/orders', label: 'Orders', Icon: IconOrders },
    { to: '/admin/purchase-orders', label: 'Purchase Orders', Icon: IconOrders },
    { to: '/admin/vendors', label: 'Vendors', Icon: IconVendors },
    { section: 'Business' },
    { to: '/admin/sales', label: 'Sales', Icon: IconOrders },
    { to: '/admin/reports', label: 'Reports', Icon: IconDashboard },
    { to: '/admin/menu', label: 'Menu', Icon: IconMenu },
    { section: 'Team' },
    { to: '/admin/users', label: 'Users', Icon: IconUsers },
    { to: '/admin/voice', label: 'Voice', Icon: IconVoice },
    { section: 'Data' },
    { to: '/admin/covers', label: 'Covers & Servers', Icon: IconDashboard },
    { section: 'System' },
    { to: '/admin/settings', label: 'Settings', Icon: IconSettings },
  ],
  super_admin: [
    { to: '/admin', label: 'Dashboard', Icon: IconDashboard },
    { section: 'Inventory' },
    { to: '/admin/inventory', label: 'Inventory', Icon: IconInventory },
    { to: '/admin/scan', label: 'Scan Invoice', Icon: IconScan },
    { to: '/admin/invoices', label: 'Invoice History', Icon: IconScan },
    { section: 'Ordering' },
    { to: '/admin/orders', label: 'Orders', Icon: IconOrders },
    { to: '/admin/purchase-orders', label: 'Purchase Orders', Icon: IconOrders },
    { to: '/admin/vendors', label: 'Vendors', Icon: IconVendors },
    { section: 'Business' },
    { to: '/admin/sales', label: 'Sales', Icon: IconOrders },
    { to: '/admin/reports', label: 'Reports', Icon: IconDashboard },
    { to: '/admin/menu', label: 'Menu', Icon: IconMenu },
    { section: 'Team' },
    { to: '/admin/users', label: 'Users', Icon: IconUsers },
    { to: '/admin/voice', label: 'Voice', Icon: IconVoice },
    { section: 'Data' },
    { to: '/admin/covers', label: 'Covers & Servers', Icon: IconDashboard },
    { section: 'System' },
    { to: '/admin/settings', label: 'Settings', Icon: IconSettings },
  ],
  staff: [
    { to: '/chef/scan', label: 'Scan Invoice', Icon: IconScan },
  ],
};

// Roles that can see the vertical toggle
const MULTI_VERTICAL_ROLES = ['restaurant_admin', 'super_admin'];

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const showVerticalToggle = MULTI_VERTICAL_ROLES.includes(user?.role);
  const isPropertyVertical = location.pathname.startsWith('/property');

  const PM_NAV = [
    { to: '/property', label: 'Dashboard', Icon: IconDashboard },
    { section: 'Properties' },
    { to: '/property/properties', label: 'Properties', Icon: IconInventory },
    { to: '/property/turnovers', label: 'Turnovers', Icon: IconScan },
    { section: 'Maintenance' },
    { to: '/property/work-orders', label: 'Work Orders', Icon: IconOrders },
    { section: 'Finance' },
    { to: '/property/owners', label: 'Owners', Icon: IconUsers },
    { to: '/property/service-workers', label: 'Service Workers', Icon: IconUsers },
    { to: '/property/leads', label: 'Lead Pipeline', Icon: IconFarmers },
  ];

  const navItems = isPropertyVertical ? PM_NAV : (NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.staff);

  function switchVertical(vertical) {
    setMobileOpen(false);
    navigate(vertical === 'property' ? '/property' : '/admin');
  }
  const initial = user?.name?.[0]?.toUpperCase() ?? 'U';

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const NavContent = () => (
    <>
      <div className="sidebar-logo">
        <ParadiseOpsLogo size={52} />
      </div>

      {/* Vertical toggle — only for multi-vertical roles */}
      {showVerticalToggle && (
        <div style={{ padding: '10px 12px 4px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <div style={{
            display: 'flex', background: 'rgba(255,255,255,0.06)',
            borderRadius: 8, padding: 3, gap: 2,
          }}>
            {[
              { key: 'restaurant', label: '🍽️ Restaurant' },
              { key: 'property', label: '🏢 Properties' },
            ].map(v => (
              <button
                key={v.key}
                onClick={() => switchVertical(v.key)}
                style={{
                  flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 4px',
                  borderRadius: 6, border: 'none', cursor: 'pointer',
                  letterSpacing: '0.01em',
                  background: (v.key === 'property') === isPropertyVertical
                    ? 'rgba(18,207,196,0.18)'
                    : 'transparent',
                  color: (v.key === 'property') === isPropertyVertical
                    ? '#12CFC4'
                    : '#64748b',
                  transition: 'all .15s',
                }}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {user?.restaurant_name && (
        <div style={{ padding: '8px 14px 10px', borderBottom: '1px solid rgba(255,255,255,.07)' }}>
          <span style={{
            display: 'inline-block',
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.03em',
            color: '#12CFC4',
            background: 'rgba(18, 207, 196, 0.10)',
            border: '1px solid rgba(18, 207, 196, 0.22)',
            borderRadius: 6,
            padding: '4px 10px',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {user.restaurant_name}
          </span>
        </div>
      )}
      <nav className="sidebar-nav">
        {navItems.map((item, i) =>
          item.section ? (
            <div key={`section-${i}`} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#334155', padding: '14px 14px 4px', marginTop: i === 0 ? 0 : 4 }}>
              {item.section}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to.split('/').length <= 2}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <span className="nav-icon"><item.Icon width={16} height={16} /></span>
              {item.label}
            </NavLink>
          )
        )}
      </nav>
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
            <div style={{ color: '#64748b', fontSize: 11, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.restaurant_name || user?.role?.toLowerCase()}</div>
          </div>
          <button className="sidebar-signout" onClick={handleLogout} title="Sign out">
            <IconSignOut width={15} height={15} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="app-shell">
      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ParadiseOpsLogo size={40} />
        </div>
        <button className="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? '✕' : '☰'}
        </button>
      </div>

      {mobileOpen && <div className="mobile-overlay" onClick={() => setMobileOpen(false)} />}

      <aside className={`sidebar${mobileOpen ? ' open' : ''}`}>
        <NavContent />
      </aside>

      <main className="main-content">{children}</main>

      {/* Floating voice button */}
      <button
        className="voice-fab"
        onClick={() => setVoiceOpen(v => !v)}
        title="Voice assistant"
      >
        {voiceOpen
          ? <IconX width={22} height={22} stroke="#fff" />
          : <IconVoice width={22} height={22} stroke="#fff" />}
      </button>

      {/* Voice drawer */}
      {voiceOpen && (
        <>
          <div className="voice-fab-overlay" onClick={() => setVoiceOpen(false)} />
          <div className="voice-fab-panel">
            <VoiceInterface
              restaurantId={user?.restaurant_id}
              compact={true}
              onClose={() => setVoiceOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
}
